import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { AuthService } from 'app/services/auth.service';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { OutgoingMessageType, SendDataMessage, IncomingMessage } from 'messages/websocket.messages';

import Message from '../message/message';
import MessageType from '../message/message-type';
import { socialAppActions } from '../state/app';

import { ChatService } from './chat.service';
import { HostService } from './host-service';
import { PhotoBoothService } from './photo-booth.service';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class SocialMessageService extends Loggable {
  private authService = inject(AuthService);
  private chatService = inject(ChatService);
  private photoBoothService = inject(PhotoBoothService);
  private hostService = inject(HostService);
  private websocketService = inject(WebsocketService);
  private store = inject<Store<AppState>>(Store);

  private user?: User;

  public constructor() {
    super();
    this.setDisplayName('SocialMessageService');
    this.authService.user$.subscribe((user) => (this.user = user));
  }

  public sendMessage(message: Message): void {
    let d = '';
    try {
      d = JSON.stringify(message);
    } catch (e) {
      this.error('Could not send message, encoding failed');
      return;
    }

    const msg: SendDataMessage = { t: OutgoingMessageType.DATA, d };
    this.websocketService.sendMessage(msg);
  }

  public sendPlainMessage(objectMessage: unknown): void {
    let msg = '';
    try {
      msg = JSON.stringify(objectMessage);
    } catch (e) {
      this.error('Could not send message, encoding failed');
      return;
    }

    this.websocketService.sendRaw(msg);
  }

  public receiveMessage(data: string): void {
    if (data) {
      const message: Message = JSON.parse(data);
      switch (message.type) {
        case MessageType.PLAYLIST_CHANGED:
          {
            const { identity: playlistId } = message;
            if (playlistId) {
              this.store.dispatch(socialAppActions.CHANGE_PLAYLIST({ playlistId }));
            }
          }
          break;

        case MessageType.PHOTO_BOOTH_START:
          if (this.photoBoothService.boothStartedByUser()) {
            this.info('Photo Booth Started by', this.user?.userId);
            return;
          }
          if (this.photoBoothService.boothInSession()) {
            return;
          }
          this.photoBoothService.handlePhotoBoothStart(message);
          break;
        case MessageType.NEW_HOST:
          this.hostService.didSomeoneMakeMeHost(message);
          break;
        default:
          break;
      }
    }
  }

  public receiveChatMessage(msg: IncomingMessage): void {
    this.chatService.wsMessageReceived(msg);
  }
}
